import { Badge, Box, Button, Container, DropdownMenu, Flex, Grid, Text } from '@radix-ui/themes'
import { type MetaArgs, type MetaFunction } from '@remix-run/node'
import {
	type ShouldRevalidateFunctionArgs,
	useLoaderData,
} from '@remix-run/react'
import isEqual from 'lodash.isequal'
import uniqWith from 'lodash.uniqwith'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { Pager } from '~/components/common/pager'
import { ProductGridItem } from '~/components/common/product-grid-item'
import { IMAGE_FILTERS } from '~/components/ui/image'
import { type GetProduct } from '~/models/ecommerce-providers/sylius/shop/fetch/product/get-product'
import { type Locale } from '~/models/locale'
import { NEWSLETTER_FORM } from '~/routes/($lang).newsletter/newsletter.component'
import { type loader } from '~/routes/($lang).products._index/index.server'
import { TaxonFilter } from '~/routes/($lang).products._index/taxon-filter.tsx'
import { ROOT_INPUTS } from '~/routes/_layout/root.component'
import { type loader as RouteLoader } from '~/routes/_layout/root.server'
import { defaultMetaI18next } from '~/utils/client/default-meta-i18next.ts'
import { imagePathFilter } from '~/utils/image-path-filter.ts'
import type { GetTaxon } from '~/models/ecommerce-providers/sylius/shop/fetch/taxon/get-taxon.ts'
import { ManufacturerFilter } from '~/routes/($lang).products._index/manufacturer-filter.tsx'
import { LeftSidebar } from '~/routes/($lang).products._index/left-sidebar.tsx'

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	routeProductsIndex = 'route-products-index',
	uiPager = 'ui-pager',
	uiWishlistAction = 'ui-wishlist-action',
}

/* -------------------------------------------------------------------------------------------------
 * Route loader
 * -----------------------------------------------------------------------------------------------*/

enum ROUTE_LOADER {
	root = 'root',
	current = 'routes/($lang).products._index',
}

/* -------------------------------------------------------------------------------------------------
 * TEST_IDS
 * -----------------------------------------------------------------------------------------------*/

enum TEST_IDS {
	filterByTaxon = 'filter-by-taxon',
	filterByTaxonSelect = 'filter-by-taxon-select',
	searchResultsLabel = 'search-results-label',
	productList = 'product-list',
}

/* -------------------------------------------------------------------------------------------------
 * Search params
 * -----------------------------------------------------------------------------------------------*/

enum SEARCH_PARAMS {
	page = 'page',
	itemsPerPage = 'itemsPerPage',
}

/* -------------------------------------------------------------------------------------------------
 * Handle
 * -----------------------------------------------------------------------------------------------*/

const handle = { i18n: [...Object.values(I18NEXT)] }

/* -------------------------------------------------------------------------------------------------
 * Meta
 * -----------------------------------------------------------------------------------------------*/

const meta: MetaFunction<
	typeof loader,
	{
		[ROUTE_LOADER.root]: typeof RouteLoader
	}
> = ({ data, matches }: MetaArgs) => {
	const parentMeta = matches.reduce((a, b) =>
		a.meta.length > b.meta.length ? a : b,
	).meta
	const title = parentMeta.find((meta) => 'title' in meta)
	const description = parentMeta.find(
		(meta) => 'name' in meta && meta.name === 'description',
	)

	const rootData = matches.find((match) => match.id === ROUTE_LOADER.root)!
		.data as { locale: Locale }

	// @ts-ignore
	return uniqWith(
		title?.title.includes('Error')
			? [...parentMeta]
			: data
				? [
						...parentMeta.filter(
							(meta) =>
								!Object.entries(meta).find(
									([key, value]) =>
										key === 'title' ||
										(key === 'name' && value === 'description'),
								),
						),
						// @ts-ignore
						{ title: `${data.meta.title} | ${title?.title}` },
						{
							name: 'description',
							// @ts-ignore
							content: `${data.meta.description} | ${description?.content}`,
						},
						// Structured data -> ld+json
						{
							// @ts-ignore
							'script:ld+json': data.products,
						},
						{
							// @ts-ignore
							'script:ld+json': data.taxons,
						},
						// Preload images
						// @ts-ignore
						...(
							data.products.items?.filter(
								({ images }: GetProduct) => !!images?.[0]?.path,
							) || []
						).map(({ images }: GetProduct) => ({
							tagname: 'link',
							rel: 'preload',
							href: imagePathFilter({
								path: images![0].path!,
								filter: IMAGE_FILTERS.gridFilter,
							}),
							as: 'image',
						})),
						// I18next
						...defaultMetaI18next<typeof I18NEXT>({
							I18NEXT,
							locale: rootData.locale,
						}),
					]
				: [
						...parentMeta.filter(
							(meta) =>
								!Object.entries(meta).find(
									([key, value]) =>
										key === 'title' ||
										(key === 'name' && value === 'description'),
								),
						),
						// @ts-ignore
						{ title: `Error | ${title?.title}` },
						{
							name: 'description',
							// @ts-ignore
							content: `Error | ${description?.content}`,
						},
					],
		(a, b) => isEqual(a, b),
	)
}

/* -------------------------------------------------------------------------------------------------
 * Should revalidate
 * -----------------------------------------------------------------------------------------------*/

const shouldRevalidate = ({
	formData,
	defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) => {
	if (formData?.get(ROOT_INPUTS.subaction) === NEWSLETTER_FORM.subaction) {
		return false
	}

	return defaultShouldRevalidate
}

/* -------------------------------------------------------------------------------------------------
 * Component
 * -----------------------------------------------------------------------------------------------*/

const Component: React.FC = () => {
	const { currentTaxon, itemsPerPage, page, products, taxons, wishlist, manufacturers, productManufacturerFiltered } =
		useLoaderData<typeof loader>()
	const { t } = useTranslation(I18NEXT.routeProductsIndex)

	return (
		<Container px="5" my="9">
			<Flex
				direction={{ initial: 'column', lg: 'row' }}
				gap={{ initial: '1', lg: '4' }}
			>
				<LeftSidebar currentTaxon={currentTaxon} taxons={taxons} />
				<Box asChild flexGrow="1">
					<article>
						<Flex
							data-testid={TEST_IDS.searchResultsLabel}
							justify="end"
							mx="5"
							mt={{ initial: '4', xs: '0' }}
							gapX="2"
						>
							<Badge variant="surface" size="1" color="gray">
								<Text>
									{t('common.foundResults-$val', {
										val: products!.hydra!['hydra:totalItems'],
									})}
								</Text>
								<Text>
									| {page * itemsPerPage - itemsPerPage + 1} -{' '}
									{Math.min(
										page * itemsPerPage,
										products!.hydra!['hydra:totalItems'],
									)}
								</Text>
							</Badge>
						</Flex>
						<Grid
							asChild
							columns={{ initial: '1', sm: '2' }}
							justify={{ initial: 'center', md: 'end' }}
							mt={{ initial: '4', md: '5' }}
							gapY={{ initial: '5', md: '4', lg: '5' }}
							gapX={{ initial: '4', md: '5', lg: '6' }}
						>
							<ul data-test-id={TEST_IDS.productList}>
								{// @ts-ignore
								products.items?.map((product: GetProduct) => (
									<ProductGridItem
										key={product.id}
										product={product}
										inWishlist={wishlist?.includes(product.id) ?? false}
										displayWishlist={typeof wishlist !== 'undefined'}
									/>
								))}
							</ul>
						</Grid>
						<Box mt="6">
							<Pager hydra={products!.hydra!['hydra:view']} />
						</Box>
					</article>
				</Box>
			</Flex>
		</Container>
	)
}
Component.displayName = 'Component'

/* -----------------------------------------------------------------------------------------------*/

export {
	Component,
	shouldRevalidate,
	handle,
	meta,
	I18NEXT as PRODUCTS_INDEX_I18NEXT,
	ROUTE_LOADER as PRODUCTS_INDEX_ROUTE_LOADER,
	TEST_IDS as PRODUCTS_INDEX_TEST_IDS,
	SEARCH_PARAMS as PRODUCTS_INDEX_SEARCH_PARAMS,
}
